import { useEffect, useRef } from "react";
import { useParams } from "@tanstack/react-router";
import { usePrintMemo } from "@/hooks/usePrintMemo";
import { useKuerzelDirectory } from "@/hooks/useKuerzelDirectory";
import { KuerzelDirectoryContext } from "@/components/memo/MemoMarkdown";
import { PrintHeader } from "@/components/memo/print/PrintHeader";
import { PrintSectionView } from "@/components/memo/print/PrintSectionView";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

/**
 * The chrome-less print route: renders a memo as a flat, static Protokoll OUTSIDE
 * the AppShell (no sidebar / topbar), wraps it in the Kürzel directory so
 * `@`-references in bodies resolve, and — once the memo AND every section history
 * have settled — auto-invokes the browser print dialog. A screen-only control bar
 * (hidden in `@media print`, see Task 5) offers manual print + back.
 *
 * Auto-print fires exactly ONCE (a ref guard, never re-armed on re-render) and only
 * after every image has settled, so the printout is never missing the masthead
 * logo. In jsdom `window.print` is undefined — the guard tolerates that.
 */
export function PrintMemoPage() {
  const { memoId } = useParams({ strict: false }) as { memoId?: UUID };
  const { doc, ready, isError } = usePrintMemo(memoId);
  const kuerzelDir = useKuerzelDirectory();
  const printedRef = useRef(false);

  useEffect(() => {
    if (!ready || printedRef.current || typeof window.print !== "function") return;
    printedRef.current = true;
    let cancelled = false;
    const pending = [...document.images]
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise<void>((resolve) => {
            img.onload = img.onerror = () => resolve();
          }),
      );
    void Promise.all(pending).then(() => {
      if (!cancelled) window.print();
    });
    return () => {
      cancelled = true;
    };
  }, [ready]);

  if (!ready || !doc) {
    return (
      <div className="memo-print-bar" style={{ padding: 24 }}>
        {t.printLoading}
      </div>
    );
  }

  return (
    <KuerzelDirectoryContext.Provider value={kuerzelDir}>
      <div className="memo-print" style={{ padding: 24 }}>
        <div
          className="memo-print-bar"
          style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}
        >
          <button type="button" onClick={() => window.print()}>
            {t.printAction}
          </button>
          <button type="button" onClick={() => history.back()}>
            {t.printBack}
          </button>
          {isError ? <span className="memo-print-hint">{t.printHistoryError}</span> : null}
        </div>

        <PrintHeader doc={doc} />

        {doc.blocks.map((block) =>
          block.sections.map((section, sectionIndex) => (
            <PrintSectionView
              key={section.anchorId}
              section={section}
              blockNumber={sectionIndex === 0 ? block.number : undefined}
              blockTitle={sectionIndex === 0 ? block.title : undefined}
            />
          )),
        )}
      </div>
    </KuerzelDirectoryContext.Provider>
  );
}
